import React from 'react';
import { Floor, LocationData, FloorQuality } from '../types';
import { FLOOR_QUALITY_TYPES } from '../constants';

interface FloorManagementProps {
  currentLocation: string;
  currentFloor: string;
  locations: Record<string, LocationData>;
  upgradeFloorQuality: (locationId: string, floorId: string, floorQualityId: string) => boolean;
  getFloorCoolingEfficiency: (floorId: string, locationId: string) => number;
  formatCurrency: (amount: number, decimals?: number) => string;
}

const FloorManagement: React.FC<FloorManagementProps> = ({
  currentLocation,
  currentFloor,
  locations,
  upgradeFloorQuality,
  getFloorCoolingEfficiency,
  formatCurrency
}) => {
  // Get the current floor data
  const locationData = locations[currentLocation];
  const floorData = locationData?.floors?.[currentFloor];
  
  if (!locationData || !floorData) {
    return <div className="p-4 bg-gray-100 rounded-lg">No floor selected</div>;
  }
  
  // Get the current floor quality
  const currentFloorQualityId = floorData.floorQualityId || 'basic';
  const currentFloorQuality = FLOOR_QUALITY_TYPES.find((fq: FloorQuality) => fq.id === currentFloorQualityId);
  
  // Calculate the cooling efficiency
  const coolingEfficiency = getFloorCoolingEfficiency(currentFloor, currentLocation);
  
  return (
    <div className="mb-6">
      {/* Current Floor Quality Status */}
      <div className="mb-4 p-4 bg-gray-800 text-white rounded-lg">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-bold">Current Floor — {locationData.name}</h3>
        </div>
        
        <div className="grid grid-cols-1 gap-2 mb-3">
          <div className="flex items-center">
            <div className={`w-4 h-4 rounded-sm bg-${currentFloorQuality?.color || 'gray'}-500 mr-2`}></div>
            <span className="font-medium">{currentFloorQuality?.badge} {currentFloorQuality?.name}</span>
            <span className="ml-2 text-sm text-gray-400">+{((coolingEfficiency - 1) * 100).toFixed(0)}% cooling capacity</span>
          </div>
        </div>

        <p className="text-xs text-gray-500">Higher floor quality increases the effective cooling capacity of your chillers, letting you run more servers before thermal limits are reached.</p>
      </div>
      
      {/* Floor Quality Selection */}
      <div className="mb-4">
        <h4 className="font-semibold mb-2 text-white bg-gray-700 p-2 rounded-t-lg">Choose Floor Quality</h4>
        <div className="bg-yellow-100 border-l-4 border-yellow-500 p-2 mb-2 text-sm text-yellow-700">
          <p><span className="font-bold">⚠️ PERMANENT UPGRADE:</span> Floor quality upgrades are <span className="font-bold">one-time purchases</span> and cannot be downgraded.</p>
        </div>
        
        <div className="border border-gray-300 rounded-b-lg overflow-hidden">
          {/* Basic Floor Option - Always show current quality */}
          <div className={`border-b border-gray-300 p-3 ${currentFloorQualityId === 'basic' ? 'bg-gray-100' : 'bg-gray-200 opacity-50'}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-gray-500 rounded-sm mr-2"></div>
                  <span className="font-medium">Basic Floor</span>
                  {currentFloorQualityId !== 'basic' && <span className="ml-2 text-xs bg-red-100 text-red-800 px-2 py-1 rounded">Downgrade Not Available</span>}
                </div>
                <div className="text-sm text-gray-600 mt-1">Standard raised floor tiles - Adequate airflow</div>
              </div>
              <div className="text-green-600 font-medium">
                {currentFloorQualityId === 'basic' ? 'Installed' : 'Free'}
              </div>
            </div>
          </div>
          
          {/* Premium Floor Option */}
          <div 
            className={`border-b border-gray-300 p-3 ${currentFloorQualityId === 'enterprise' ? 'bg-gray-200 opacity-50' : currentFloorQualityId !== 'premium' ? 'cursor-pointer hover:bg-blue-50' : 'bg-blue-50'}`}
            onClick={() => currentFloorQualityId !== 'premium' && currentFloorQualityId !== 'enterprise' && upgradeFloorQuality(currentLocation, currentFloor, 'premium')}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-blue-500 rounded-sm mr-2"></div>
                  <span className="font-medium">Premium Floor</span>
                  {currentFloorQualityId === 'enterprise' && <span className="ml-2 text-xs bg-red-100 text-red-800 px-2 py-1 rounded">Downgrade Not Available</span>}
                  {currentFloorQualityId !== 'premium' && currentFloorQualityId !== 'enterprise' && <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">PERMANENT UPGRADE</span>}
                </div>
                <div className="text-sm text-gray-600 mt-1">High-airflow perforated tiles — improved airflow management</div>
                <div className="text-sm text-blue-600 mt-1">+25% cooling boost</div>
              </div>
              <div className={`${currentFloorQualityId === 'premium' ? 'text-blue-600' : 'text-green-600'} font-medium`}>
                {currentFloorQualityId === 'premium' ? 'Installed' : formatCurrency(75000)}
              </div>
            </div>
          </div>
          
          {/* Enterprise Floor Option */}
          <div 
            className={`p-3 ${currentFloorQualityId !== 'enterprise' ? 'cursor-pointer hover:bg-blue-50' : 'bg-blue-50'}`}
            onClick={() => currentFloorQualityId !== 'enterprise' && upgradeFloorQuality(currentLocation, currentFloor, 'enterprise')}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-purple-500 rounded-sm mr-2"></div>
                  <span className="font-medium">Enterprise Floor</span>
                  {currentFloorQualityId !== 'enterprise' && <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">PERMANENT UPGRADE</span>}
                </div>
                <div className="text-sm text-gray-600 mt-1">Advanced airflow management — full hot-aisle/cold-aisle containment</div>
                <div className="text-sm text-blue-600 mt-1">+50% cooling boost</div>
              </div>
              <div className={`${currentFloorQualityId === 'enterprise' ? 'text-blue-600' : 'text-green-600'} font-medium`}>
                {currentFloorQualityId === 'enterprise' ? 'Installed' : formatCurrency(250000)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FloorManagement;
